<template lang="pug">
aiq-dialog.field-settings-modal(
    :title="$t(`${baseTranslationPath}.field_settings_title`)"
    :model-value="modelValue"
    :show-close="true"
    :close-on-click-modal="false"
    width="480px"
    @update:model-value="close"
)
    .f-form(v-if="field")
        .f-row
            label {{ $t(`${baseTranslationPath}.field_name`) }} *
            aiq-input(
                v-model="form.field_name"
                :placeholder="$t(`${baseTranslationPath}.field_name_ph`)"
            )
        .f-row
            label {{ $t(`${baseTranslationPath}.field_type`) }} *
            aiq-select(v-model="form.field_type" style="width: 100%" filterable)
                aiq-option(v-for="t in fieldTypes" :key="t" :label="t" :value="t")
        .f-row
            label {{ $t(`${baseTranslationPath}.interaction_class`) }}
            aiq-select(v-model="form.interaction_class" style="width: 100%" clearable filterable)
                aiq-option(v-for="c in interactionClasses" :key="c" :label="c" :value="c")
            .f-hint {{ $t(`${baseTranslationPath}.interaction_class_hint`) }}
        .f-row
            label {{ $t(`${baseTranslationPath}.field_question`) }}
            aiq-select(
                v-model="form.question_id"
                style="width: 100%"
                clearable
                filterable
                :placeholder="$t(`${baseTranslationPath}.field_question_ph`)"
            )
                aiq-option(
                    v-for="q in questions"
                    :key="q.question_key"
                    :label="questionLabel(q)"
                    :value="q.question_key"
                )
        .f-row
            label {{ $t(`${baseTranslationPath}.match_status`) }}
            aiq-select(v-model="form.match_status" style="width: 100%" filterable)
                aiq-option(v-for="s in matchStatuses" :key="s" :label="s" :value="s")
        .f-row.inline
            aiq-switch(v-model="form.required")
            span {{ $t(`${baseTranslationPath}.required`) }}
        .f-row.inline
            aiq-switch(v-model="form.needs_review")
            span {{ $t(`${baseTranslationPath}.needs_review`) }}
        .f-readonly
            .ro-row
                span.ro-label {{ $t(`${baseTranslationPath}.field_key`) }}
                span.ro-value {{ field.field_key }}
            .ro-row(v-if="field.source")
                span.ro-label {{ $t(`${baseTranslationPath}.field_source`) }}
                span.ro-value {{ field.source }}
            .ro-row(v-if="mappedKeys.length")
                span.ro-label {{ $t(`${baseTranslationPath}.field_mapped_keys`) }}
                span.ro-value {{ mappedKeys.join(', ') }}
            .ro-row(v-if="suggestedKeys.length")
                span.ro-label {{ $t(`${baseTranslationPath}.field_suggested_keys`) }}
                span.ro-value {{ suggestedKeys.join(', ') }}
        .actions
            aiq-button.delete-btn(type="danger" plain :loading="deleting" @click="onDelete") {{ $t(`${baseTranslationPath}.delete_field`) }}
            .actions-right
                aiq-button(@click="close") {{ $t('global.cancel') }}
                aiq-button(type="primary" :loading="saving" :disabled="!canSubmit" @click="submit") {{ $t('global.save') }}
</template>
<script>
import get from 'lodash/get';

const FIELD_TYPES = ['text', 'checkbox', 'radio', 'date', 'currency', 'phone', 'email',
  'account_number', 'routing_number', 'signature', 'initials', 'table_cell', 'computed',
  'internal_only', 'transaction'];
const INTERACTION_CLASSES = ['simple_input', 'long_text', 'boolean_choice', 'single_choice',
  'multi_choice', 'signature', 'computed', 'internal_only', 'repeating_row', 'repeating_group',
  'composite_group', 'conditional_detail'];
const MATCH_STATUSES = ['auto_matched', 'ambiguous', 'unmatched', 'not_applicable', 'confirmed'];

export default {
  name: 'FieldSettingsDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    formId: {
      type: [String, Number],
      default: null,
    },
    // the selected form_fields row being edited.
    field: {
      type: Object,
      default: null,
    },
    // all form_questions rows for the form (to (re)link this field).
    questions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'updated', 'deleted'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
      fieldTypes: FIELD_TYPES,
      interactionClasses: INTERACTION_CLASSES,
      matchStatuses: MATCH_STATUSES,
      form: this.emptyForm(),
      saving: false,
      deleting: false,
    };
  },
  computed: {
    // question_key of the question this field is currently linked to (via question_ref).
    currentQuestionKey() {
      const ref = get(this.field, 'question_ref');
      if (!ref) {
        return null;
      }
      const owner = this.questions.find(q => q.id === ref);
      return get(owner, 'question_key', null);
    },
    mappedKeys() {
      const keys = get(this.field, 'mapped_keys');
      return Array.isArray(keys) ? keys : [];
    },
    suggestedKeys() {
      const keys = get(this.field, 'suggested_keys');
      return Array.isArray(keys) ? keys : [];
    },
    canSubmit() {
      return !!(this.form.field_name && this.form.field_name.trim()) && !!this.form.field_type;
    },
  },
  watch: {
    // re-seed the form whenever the dialog opens or the selected field changes.
    modelValue(open) {
      if (open) {
        this.seed();
      }
    },
    field() {
      if (this.modelValue) {
        this.seed();
      }
    },
  },
  methods: {
    emptyForm() {
      return {
        field_name: '',
        field_type: 'text',
        interaction_class: null,
        question_id: null,
        match_status: 'unmatched',
        required: false,
        needs_review: false,
      };
    },
    seed() {
      this.form = {
        field_name: get(this.field, 'field_name') || get(this.field, 'label') || '',
        field_type: get(this.field, 'field_type') || 'text',
        interaction_class: get(this.field, 'interaction_class') || null,
        question_id: this.currentQuestionKey,
        match_status: get(this.field, 'match_status') || 'unmatched',
        required: Boolean(get(this.field, 'required')),
        needs_review: Boolean(get(this.field, 'needs_review')),
      };
    },
    questionLabel(question) {
      return get(question, 'prompt') || get(question, 'question_key');
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    // Only send fields that actually changed.
    buildChanges() {
      const body = {};
      const name = this.form.field_name.trim();
      if (name !== (get(this.field, 'field_name') || get(this.field, 'label') || '')) {
        body.field_name = name;
      }
      if (this.form.field_type !== get(this.field, 'field_type')) {
        body.field_type = this.form.field_type;
      }
      if ((this.form.interaction_class || null) !== (get(this.field, 'interaction_class') || null)) {
        body.interaction_class = this.form.interaction_class || null;
      }
      if ((this.form.question_id || null) !== (this.currentQuestionKey || null)) {
        body.question_id = this.form.question_id || null;
      }
      if (this.form.match_status !== get(this.field, 'match_status')) {
        body.match_status = this.form.match_status;
      }
      if (this.form.required !== Boolean(get(this.field, 'required'))) {
        body.required = this.form.required;
      }
      if (this.form.needs_review !== Boolean(get(this.field, 'needs_review'))) {
        body.needs_review = this.form.needs_review;
      }
      return body;
    },
    async submit() {
      if (!this.canSubmit || !this.formId || !this.field) {
        return;
      }
      const body = this.buildChanges();
      if (!Object.keys(body).length) {
        this.close();
        return;
      }
      this.saving = true;
      try {
        await this.$store.dispatch('agentForms/updateFormField', {
          formId: this.formId,
          fieldId: this.field.id,
          ...body,
        });
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.field_updated`));
        this.$emit('updated', this.field.id);
        this.close();
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.field_update_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.saving = false;
      }
    },
    async onDelete() {
      if (!this.formId || !this.field) {
        return;
      }
      const name = get(this.field, 'field_name') || get(this.field, 'label') || get(this.field, 'field_key');
      const confirmed = await this.$aiq.confirm(
        this.$t(`${this.baseTranslationPath}.delete_field_title`),
        this.$t(`${this.baseTranslationPath}.delete_field_confirm`, { name }),
      ).catch(() => false);
      if (!confirmed) {
        return;
      }
      this.deleting = true;
      try {
        await this.$store.dispatch('agentForms/deleteFormField', {
          formId: this.formId,
          fieldId: this.field.id,
        });
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.field_deleted`));
        this.$emit('deleted', this.field.id);
        this.close();
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.field_delete_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.f-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.f-row {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    font-weight: 600;
  }

  &.inline {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
}

.f-hint {
  font-size: 11px;
  color: $aiq-color-text-secondary;
}

.f-readonly {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  border: 1px solid $aiq-icon-primary;
  border-radius: 5px;
  background-color: $aiq-tab-content-background;
}

.ro-row {
  display: flex;
  gap: 8px;
  font-size: 12px;
}

.ro-label {
  font-weight: 600;
  flex-shrink: 0;
  color: $aiq-color-text-secondary;
}

.ro-value {
  min-width: 0;
  word-break: break-word;
}

.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.actions-right {
  display: flex;
  gap: 8px;
}
</style>
